/**
 * chatIntentRouter — pure routing decision for intent-routed chat.
 *
 * The backend classifier (/api/chat-intent) already downgrades low
 * confidence and validates against the candidate set, but the client
 * re-validates here — defense in depth against a stale answer (e.g. the
 * run left post-run between submit and response, or the cursor section
 * never resolved to a module). Fallback is ALWAYS plain chat.
 *
 * Pure module (no hooks, no I/O) so route decisions are unit-testable.
 */

import type { ChatIntentResponse, ChatRoutedIntent } from "../api/client";

export interface RouteContext {
  runId: string | null;
  /** Variant C run state — section routes require "post-run". */
  runState: string;
  /** Module id the cursor section resolved to (spine section id). */
  sectionId: string | null;
  /** Section number the cursor is in (for Word-apply targeting). */
  sectionNumber: string | null;
  /** Captured highlight text, if any. */
  selectedText: string | null;
  /** Spine status of the target section, when known (e.g. "drafting"). */
  sectionStatus?: string | null;
}

export type RouteAction =
  | { kind: "chat"; mode?: "default" | "research" }
  | {
      kind: "edit_section";
      runId: string;
      sectionId: string;
      sectionNumber: string | null;
      instruction: string;
    }
  | {
      kind: "draft_content";
      runId: string;
      sectionId: string;
      sectionNumber: string | null;
      instruction: string;
    }
  | {
      kind: "edit_highlight";
      runId: string | null;
      sectionId: string | null;
      sectionNumber: string | null;
      selectedText: string;
      instruction: string;
    };

/** True when the context allows targeting a section with regen/ad-hoc. */
export function canTargetSection(ctx: RouteContext): boolean {
  return Boolean(
    ctx.runId && ctx.runState === "post-run" && ctx.sectionId && ctx.sectionStatus !== "drafting",
  );
}

// ---------------------------------------------------------------------------
// Classifier-unavailable fallback
// ---------------------------------------------------------------------------
// Mirror (regexes verbatim) of ``_heuristic_highlight_intent`` in
// server/routes/chat_intent.py: a highlight plus an imperative rewrite
// verb aimed at it is unambiguous enough to route to the edit flow
// without the model. The server applies it BEFORE its LLM call; the
// client only needs it when the server never answered (5s client
// timeout, transport error) — otherwise the server's decision stands.
// Keep the two in sync.

const POLITENESS_PREFIX = "(?:(?:please|pls|now|also|just|kindly|can you|could you|would you)\\s+)*";
const REWRITE_VERBS =
  "(?:rewrite|re-write|redraft|re-draft|rephrase|reword|revise|rework|edit|update|replace|" +
  "correct|fix|fill(?:\\s+in)?|resolve|shorten|expand|tighten|condense|simplify|polish|" +
  "improve|change)";
// Clause must START with the verb (after politeness); "update me on…"
// and "should I rewrite…" are not edit requests.
const REWRITE_CLAUSE_RE = new RegExp(`^\\s*${POLITENESS_PREFIX}${REWRITE_VERBS}\\b(?!\\s+(?:me|us)\\b)`, "i");
// Sentence punctuation and the conjunctions that chain asks.
const CLAUSE_SPLIT_RE = /[.;!?\n]+|\b(?:and|then)\b/i;
// The message must point at the highlight (or the flags inside it).
const HIGHLIGHT_REF_RE = new RegExp(
  "\\b(?:highlight(?:ed|s)?" +
    "|this\\s+(?:portion|part|text|paragraph|passage|sentence|span|bit|section)" +
    "|these\\s+(?:flags|markers|gaps|placeholders|sentences|paragraphs)" +
    "|the\\s+(?:flags|markers|missing[- ]info\\s+flags|placeholders|selected\\s+text|selection|text))\\b",
  "i",
);

/** Reasons meaning the server never delivered a decision. */
const CLASSIFIER_UNAVAILABLE_REASONS = new Set(["client_timeout", "client_error"]);

/**
 * ``"edit_highlight"`` when some clause of ``text`` is an imperative
 * rewrite aimed at the highlight; else null. Pure; assumes a highlight
 * exists (callers check ``selectedText``).
 */
export function heuristicHighlightIntent(text: string): "edit_highlight" | null {
  const message = text.trim();
  if (!message || !HIGHLIGHT_REF_RE.test(message)) return null;
  for (const clause of message.split(CLAUSE_SPLIT_RE)) {
    if (clause && REWRITE_CLAUSE_RE.test(clause)) return "edit_highlight";
  }
  return null;
}

/**
 * Upgrade a classifier response that never reached a decision
 * (client timeout / transport error) to ``edit_highlight`` when the
 * context has a highlight and the message reads as a rewrite of it.
 * Any real server decision — match, fallback_chat, low_confidence,
 * gated, heuristic… — is returned untouched.
 */
export function withClassifierFallback(
  response: ChatIntentResponse,
  ctx: RouteContext,
  text: string,
): ChatIntentResponse {
  if (!CLASSIFIER_UNAVAILABLE_REASONS.has(response.reason)) return response;
  if (!ctx.selectedText || !ctx.selectedText.trim()) return response;
  const intent = heuristicHighlightIntent(text);
  if (intent === null) return response;
  return {
    ...response,
    intent,
    classified_intent: intent,
    confidence: "high",
    reason: "client_heuristic",
  };
}

/**
 * Decide the route for a classified intent. ``text`` is the writer's
 * message (becomes guidance / instruction on section routes).
 */
export function decideRoute(
  response: ChatIntentResponse,
  ctx: RouteContext,
  text: string,
): RouteAction {
  const intent: ChatRoutedIntent = response.intent;

  switch (intent) {
    case "edit_section": {
      if (!canTargetSection(ctx)) return { kind: "chat" };
      return {
        kind: "edit_section",
        runId: ctx.runId as string,
        sectionId: ctx.sectionId as string,
        sectionNumber: ctx.sectionNumber,
        instruction: text,
      };
    }
    case "draft_content": {
      if (!canTargetSection(ctx)) return { kind: "chat" };
      return {
        kind: "draft_content",
        runId: ctx.runId as string,
        sectionId: ctx.sectionId as string,
        sectionNumber: ctx.sectionNumber,
        instruction: text,
      };
    }
    case "edit_highlight": {
      if (!ctx.selectedText || !ctx.selectedText.trim()) return { kind: "chat" };
      // Highlight edits do NOT require a run/section — without one the
      // change applies to Word only (untracked), which the flow surfaces.
      return {
        kind: "edit_highlight",
        runId: ctx.runId,
        sectionId: ctx.sectionId,
        sectionNumber: ctx.sectionNumber,
        selectedText: ctx.selectedText,
        instruction: text,
      };
    }
    case "research_highlight": {
      if (!ctx.selectedText || !ctx.selectedText.trim()) return { kind: "chat" };
      return { kind: "chat", mode: "research" };
    }
    default:
      return { kind: "chat" };
  }
}
